import C from "@/components/sidebar/StandardSidebar.module.css";

import { BadgeWithTip } from "@/components/Badge.tsx";
import { localeCompareBy } from "@/components/data/comparers.ts";
import { getFilteredItems } from "@/components/data/getFilteredItems.ts";
import { getSortedItems, type SimpleSortField } from "@/components/data/getSortedItems.ts";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { EmptyItem } from "@/components/sidebar/EmptyItem.tsx";
import { ListResult, SidebarList, type SidebarListState } from "@/components/sidebar/SidebarList.tsx";
import { StandardBlock } from "@/components/sidebar/StandardSidebar.tsx";
import { ResourceCreateLinks } from "@/modules/api/resourceCreateLinks.ts";
import { BillingAccountIcon, ShortInfoList } from "@/modules/billing/BillingAccountInfo.tsx";
import { eBillingAccountQueryAtom } from "@/modules/billing/billingAccountQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { billingAccountLink } from "@/modules/billing/links.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { unixToDate } from "@warrenio/api-spec/conversion";
import { useAtomValue } from "jotai/react";

const sortFields = [
    {
        id: "name",
        title: <Trans>Name</Trans>,
        compare: localeCompareBy((i) => i.title),
    },
    { id: "created", title: <Trans>Created date</Trans>, getValue: (i) => unixToDate(i.account.created).getTime() },
    { id: "unpaid_amount", title: <Trans>Outstanding amount</Trans>, getValue: (i) => i.unpaidAmount ?? 0 },
] satisfies SimpleSortField<EBillingAccount>[];

export default function BillingList() {
    const { t } = useLingui();
    return (
        <SidebarList
            data={(props) => <BillingData {...props} />}
            title={t`Billing`}
            sortFields={sortFields}
            actionButton={{
                title: t`Create new`,
                action: ResourceCreateLinks.billing_account,
            }}
            listId="billing"
        />
    );
}

export function BillingData({ queryString, sortId, sortAsc }: SidebarListState) {
    const query = useAtomValue(eBillingAccountQueryAtom);

    let billingItems = getFilteredItems(queryString, [...(query.data?.values() ?? [])], (item) => [item.title]);
    billingItems = getSortedItems(sortFields, { sortId, sortAsc }, billingItems);

    return (
        <ListResult
            items={billingItems.map((obj) => (
                <BillingBlock key={obj.id} item={obj} />
            ))}
            isSearching={!!queryString}
            query={query}
            emptyItem={
                <EmptyItem icon="jp-stack-icon">
                    <Trans>Create a billing account first, then you can start creating resources.</Trans>
                </EmptyItem>
            }
        />
    );
}

export function BillingBlock({ item }: { item: EBillingAccount }) {
    const { t } = useLingui();
    const { title, isDefault, unpaidAmount, ongoingAmount } = item;

    return (
        <StandardBlock link={billingAccountLink(item)}>
            <div className={C.StandardBlockIcon}>
                <BillingAccountIcon account={item} primaryColor="" />
            </div>

            <div className={cn(C.StandardBlockName, "text-ellipsis")}>
                <b className="text-ellipsis">{title}</b>
                <ShortInfoList account={item} />
            </div>

            {!!unpaidAmount && (
                <div className={C.StandardBlockInfo}>
                    <div className="text-muted">
                        <Trans>Unpaid</Trans>
                    </div>
                    <span className="color-error font-size-default">
                        <CurrencyBalance value={-unpaidAmount} />
                    </span>
                </div>
            )}

            <div className={C.StandardBlockInfo}>
                <div className="text-muted">
                    <Trans>Ongoing</Trans>
                </div>
                <span className={ongoingAmount > 0 ? "color-success" : ""}>
                    <CurrencyBalance value={ongoingAmount} />
                </span>
            </div>

            {isDefault && (
                <div className={C.holder}>
                    <BadgeWithTip description={t`Default`} noDot iconOnly inList>
                        <MaskIcon className="jp-icon-checkmark size-0.75rem" />
                    </BadgeWithTip>
                </div>
            )}
        </StandardBlock>
    );
}
